import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Music, FileText } from "lucide-react";
import { format } from "date-fns";
import { apiClient } from "@/App";
import { gigLocalDate } from "@/utils/dateUtils";

function parseGigDate(dateStr) {
  const localDateStr = gigLocalDate(dateStr);
  if (!localDateStr) return null;
  const parts = localDateStr.split("-");
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return null;
}

export default function GigInviteView() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [gig, setGig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await apiClient.get(`/rsvp/${token}`);
        setGig(res.data.gig);
        // "invited" (sent, awaiting response) must still show the RSVP
        // buttons — only "confirmed"/"declined" are a final answer.
        if (["confirmed", "declined"].includes(res.data.invitation?.status)) {
          setRsvpStatus(res.data.invitation.status);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [token]);

  // Auto-submit if ?response=yes|no from email button
  useEffect(() => {
    const autoResponse = searchParams.get("response");
    if (!autoResponse || !gig || rsvpStatus || submitting) return;
    if (autoResponse === "yes" || autoResponse === "no") {
      handleRsvp(autoResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gig]);

  const handleRsvp = async (response) => {
    setSubmitting(true);
    try {
      await apiClient.post(`/rsvp/${token}?response=${response}`);
      setRsvpStatus(response === "yes" ? "confirmed" : "declined");
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !gig) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="bg-card border-border max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Music className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-xl font-semibold mb-2">Gig not found</h2>
            <p className="text-muted-foreground text-sm">
              This invite link is invalid or has expired.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gigDate = parseGigDate(gig.date);
  const formattedDate = gigDate ? format(gigDate, "EEEE, MMMM d, yyyy") : "Date TBA";
  const location = [gig.venue_name, gig.city].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto mt-8">
        {/* Fretless Banner */}
        <div className="mb-6 p-4 bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 rounded-lg">
          <p className="text-xs font-semibold text-primary mb-2">✨ Powered by Fretless</p>
          <p className="text-sm text-foreground/90 mb-3">
            This is <strong>Fretless</strong> — the musician's command center for managing gigs. See live updates as the gig owner makes changes.
          </p>
          <a
            href="/"
            className="inline-block text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            Create free account to manage all your gigs →
          </a>
        </div>

        <Card className="bg-card border-border overflow-hidden">
          {/* Header with artist name */}
          <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-8 text-white">
            <h1 className="text-3xl font-bold mb-2">{gig.artist_name}</h1>
            <p className="text-primary-foreground/90 opacity-90">You're invited to play</p>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Gig Details */}
            <div className="space-y-4">
              {gig.venue_name && (
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{gig.venue_name}</p>
                    {gig.venue_address && (
                      <p className="text-sm text-muted-foreground">{gig.venue_address}</p>
                    )}
                    {gig.city && (
                      <p className="text-sm text-muted-foreground">{gig.city}</p>
                    )}
                  </div>
                </div>
              )}

              {gig.date && (
                <div className="flex gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{formattedDate}</p>
                  </div>
                </div>
              )}

              {gig.start_time && (
                <div className="flex gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">
                      {gig.start_time}
                      {gig.end_time && ` – ${gig.end_time}`}
                    </p>
                  </div>
                </div>
              )}

              {gig.call_time && (
                <div className="flex gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Call time</p>
                    <p className="text-sm font-medium">{gig.call_time}</p>
                  </div>
                </div>
              )}

              {gig.dress_code && (
                <div className="flex gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Dress code</p>
                    <p className="text-sm font-medium">{gig.dress_code}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            {gig.gig_notes && (
              <div className="border-t pt-4">
                <p className="text-xs font-medium text-muted-foreground mb-2">Notes</p>
                <p className="text-sm">{gig.gig_notes}</p>
              </div>
            )}

            {/* RSVP Buttons */}
            {!rsvpStatus ? (
              <div className="border-t pt-6 space-y-3">
                <p className="text-sm font-medium">Confirm your availability</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleRsvp("yes")}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    ✓ I'm in
                  </Button>
                  <Button
                    onClick={() => handleRsvp("no")}
                    disabled={submitting}
                    variant="outline"
                  >
                    ✕ Can't make it
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-t pt-6 bg-muted/50 p-4 rounded">
                <p className="text-sm font-medium">
                  {rsvpStatus === "confirmed" ? "✓ You're confirmed!" : "✕ You've declined"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {rsvpStatus === "confirmed"
                    ? "See you there!"
                    : "Thanks for letting us know."}
                </p>
              </div>
            )}

            {/* Footer note */}
            <p className="text-xs text-muted-foreground text-center border-t pt-4">
              Invitation sent via Fretless
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
